import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  AgeGroup,
  AGE_GROUPS,
  CreateJobRequest,
  createJobRequestSchema,
  isMaterialMethod,
  JOB_APPROVALS,
  JOB_REQUEST_MAX,
  JOB_TASK_COUNT_MAX,
  JobApproval,
  JobMethod,
  Language,
  LANGUAGES,
  MATERIAL_MAX_CHARS,
  ProblemCode,
  StepModelChoices,
  StudyMaterialText,
} from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { useListDocumentsQuery } from '../../document/documentEndpoints';
import { useListSourcesQuery } from '../../source/sourceEndpoints';
import {
  MaterialPrepError,
  MaterialPrepErrorCode,
  PreparedMaterial,
} from '../extraction/extractionTypes';
import { hashFile } from '../extraction/hashFile';
import { useCreateExtractionUploadMutation, useCreateJobMutation } from '../jobEndpoints';
import { MaterialNameFields } from '../MaterialNameFields';
import { loadNewJobDefaults, saveNewJobDefaults } from '../newJobDefaults';
import {
  charsInRange,
  PageRangeInput,
  parseRange,
  PdfMaterialSection,
} from '../PdfMaterialSection';
import { PhotoMaterialSection, photoSetChars } from '../PhotoMaterialSection';
import { photoSetExtraction } from '../photo/photoSet';
import { PhotoSet } from '../photo/photoTypes';
import { RequestFileField } from '../RequestFileField';
import { StepModelsField } from '../StepModelsField';
import { uploadExtraction } from '../uploadExtraction';

const isAbortError = (thrown: unknown): boolean =>
  thrown instanceof DOMException && thrown.name === 'AbortError';

/** A photo set's text hashed through SubtleCrypto, as a file is (PHO-D6). */
const hashText = (s: string): Promise<string> => hashFile(new Blob([s]));

/** The shape a zod issue needs for `mapIssues`; `ZodIssue` satisfies this. */
interface Issue {
  path: readonly PropertyKey[];
  message: string;
}

/** `createJobRequestSchema`'s top-level keys, mapped to the form's field ids (QZ-D48, PDG-D9). */
const FIELD_IDS: Record<string, string> = {
  language: 'job-language',
  taskCount: 'job-task-count',
  request: 'job-request',
  stepModels: 'job-step-models',
  sourceSites: 'job-sources',
  targetLanguages: 'job-targetLanguages',
  ageGroups: 'job-ageGroups',
  approval: 'job-approval',
  instructions: 'job-instructions',
  extractionId: 'job-material',
  materialName: 'job-document',
  section: 'job-section',
  pageFrom: 'job-material',
  pageTo: 'job-material',
  material: 'job-material',
  requestFile: 'job-requestFile',
};

/**
 * The `POST /jobs` problem codes about a material method's PDF or photo set, shown on its field
 * rather than the form (QZ-D91).
 */
const MATERIAL_CODES: ReadonlySet<ProblemCode> = new Set<ProblemCode>([
  'InvalidPageRange',
  'MaterialTooLarge',
  'InvalidMaterial',
  'ExtractionNotFound',
]);

/** The `MATERIAL_CODES` a range edit makes stale, since they were about the range sent. */
const RANGE_CODES: ReadonlySet<ProblemCode> = new Set<ProblemCode>([
  'InvalidPageRange',
  'MaterialTooLarge',
]);

/** Maps the schema's issues to the form's field ids, keyed by `issue.path[0]` (QZ-D48). */
const mapIssues = (issues: readonly Issue[]): Record<string, string[]> => {
  const fields: Record<string, string[]> = {};
  for (const issue of issues) {
    const id = FIELD_IDS[String(issue.path[0])];
    if (!id) {
      continue;
    }
    const list = fields[id] ?? [];
    if (!list.includes(issue.message)) {
      fields[id] = [...list, issue.message];
    }
  }
  return fields;
};

/** A field's issues, shown under it, as `TaskForm`'s `FieldError` does. */
const FieldError = ({ id, messages }: { id: string; messages?: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={`${id}-error`} className="invalid-feedback d-block">
      {messages.map((message, index) => (
        <div key={index}>{message}</div>
      ))}
    </div>
  );
};

/**
 * The `/jobs/new` form (QZ-D48, PDG-D9): a method chooser, a job language (QZ-D22, default
 * `sv`), a task count and the model that writes the tasks, plus either a request and the source
 * sites to research it on, none meaning the open web (prompt, QZ-D48), or a PDF whose text is
 * prepared in this browser, with an optional page range (PDF, QZ-D80, QZ-D81, QZ-D82), or a set
 * of page photos read by OCR in this browser (photos, QZ-D91). A PDF or photo job names its
 * document and, optionally, a section of it, shared by both methods and suggested from the
 * catalogue (QZ-D100, QZ-D101, QZ-D103). The photos' page language follows the job language
 * until the first photo is added (PHO-D1); Start waits for every photo to be read within the
 * limit (PHO-D5), then uploads the set's text as an extraction before creating the job
 * (PHO-D6). Follows `job-new.html`'s three states. A started job's settings, but nothing that
 * belongs to that one job, are where the next New Job in this browser starts (`newJobDefaults`).
 */
export const NewJobPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: sourcesData } = useListSourcesQuery();
  // Suggests every catalogued document, whatever its counts (QZ-D103); `[]` while it loads or
  // fails, so the boxes still work, unsuggested.
  const { data: documentsData } = useListDocumentsQuery();
  const [createJob, { isLoading }] = useCreateJobMutation();
  const [createExtractionUpload] = useCreateExtractionUploadMutation();

  // The settings the last job started in this browser left behind, read once.
  const [initial] = useState(() => loadNewJobDefaults());
  const [method, setMethod] = useState<JobMethod>(initial.method);
  const [language, setLanguage] = useState<Language>(initial.language);
  const [taskCount, setTaskCount] = useState(String(initial.taskCount));
  const [request, setRequest] = useState('');
  // The request's optional file (QZ-D141); never part of `saveNewJobDefaults`, since it belongs
  // to the one job it was picked for (RQF-D3).
  const [requestFile, setRequestFile] = useState<{ name: string; text: string } | undefined>();
  const [stepModels, setStepModels] = useState<StepModelChoices>(initial.stepModels);
  const [approval, setApproval] = useState<JobApproval>(initial.approval);
  // Every language and age group until a job is started (QZ-D130, QZ-D131); changing the job
  // language locks the new language's chip, adding it if missing, without unticking the old one
  // (TMA-D5).
  const [targetLanguages, setTargetLanguages] = useState<Language[]>(initial.targetLanguages);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>(initial.ageGroups);
  const [material, setMaterial] = useState<PreparedMaterial | undefined>();
  const [range, setRange] = useState<PageRangeInput>({ from: '', to: '' });
  // The server's problem code about the PDF, kept as a code so a range edit can tell whether
  // it was about the range.
  const [materialErrorCode, setMaterialErrorCode] = useState<ProblemCode | undefined>();
  const [instructions, setInstructions] = useState('');
  // Shared by the PDF and photo methods, and kept across a method switch (QZ-D100).
  const [document, setDocument] = useState('');
  const [section, setSection] = useState('');
  // The last prepared PDF's file name, so a later pick knows whether the box still follows it
  // (QZ-D100).
  const lastFileName = useRef('');
  // The sites unticked, not those ticked, so every site starts ticked once the list loads.
  const [unpicked, setUnpicked] = useState<ReadonlySet<string>>(
    () => new Set(initial.unpickedSites)
  );
  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | undefined>();
  // The set and its language survive a method switch, as the prepared PDF does.
  const [photoSet, setPhotoSet] = useState<PhotoSet>({ photos: [] });
  // `undefined` follows the job language; an explicit pick, or the first photo added, pins it
  // (PHO-D1), so a later job language change does not read every photo again.
  const [photoPageLanguage, setPhotoPageLanguage] = useState<Language | undefined>();
  const [photoError, setPhotoError] = useState<MaterialPrepErrorCode | undefined>();
  const [uploading, setUploading] = useState(false);
  // The photo submit in flight; a method switch or leaving the page aborts it.
  const uploadRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => () => uploadRef.current?.abort(), []);

  const pageLanguage = photoPageLanguage ?? language;

  const sourcesLoaded = sourcesData !== undefined;
  const sources = sourcesData?.sources ?? [];
  const pickedSites = sources
    .map((source) => source.domain)
    .filter((domain) => !unpicked.has(domain));
  // Nothing ticked is the open web; before the list loads nothing is known to be unticked.
  const openWeb = sourcesLoaded && pickedSites.length === 0;

  const toggleSite = (domain: string, checked: boolean) => {
    setUnpicked((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  // TMA-D5: the new job language's chip locks and ticks; the old one unlocks but stays ticked.
  const handleLanguageChange = (next: Language) => {
    setLanguage(next);
    setTargetLanguages((prev) => (prev.includes(next) ? prev : [...prev, next]));
  };

  const toggleTargetLanguage = (code: Language, checked: boolean) => {
    setTargetLanguages((prev) => (checked ? [...prev, code] : prev.filter((l) => l !== code)));
  };

  const toggleAgeGroup = (ageGroup: AgeGroup, checked: boolean) => {
    setAgeGroups((prev) => (checked ? [...prev, ageGroup] : prev.filter((a) => a !== ageGroup)));
  };
  // PTX-D3: Start waits for a prepared PDF, a document and a valid range with some text, within
  // the limit (QZ-D100). A job with no text can only fail its plan.
  const parsedRange = material ? parseRange(range, material.pdfPageCount) : undefined;
  const rangeChars =
    material && parsedRange
      ? charsInRange(material.pageChars, parsedRange.from, parsedRange.to)
      : 0;
  const pdfReady = document.trim() !== '' && rangeChars > 0 && rangeChars <= MATERIAL_MAX_CHARS;
  // PHO-D5: Start waits for a document and a set whose every photo is read, with some text,
  // within the limit (QZ-D100).
  const setChars = photoSetChars(photoSet);
  const photosReady =
    document.trim() !== '' &&
    photoSet.photos.length > 0 &&
    photoSet.photos.every((photo) => photo.status === 'ready') &&
    setChars > 0 &&
    setChars <= MATERIAL_MAX_CHARS;

  // A new PDF makes every earlier error about the material stale, the form's zod ones included.
  const clearMaterialFieldErrors = () =>
    setFields((prev) => {
      if (!prev['job-material']) {
        return prev;
      }
      const next = { ...prev };
      delete next['job-material'];
      return next;
    });

  // QZ-D100: a prepared PDF's file name fills the document box, but only while the admin has
  // not touched it — an empty box, or one still holding the previously prepared file's name.
  // `previous` is read before `lastFileName` is updated, since the updater below runs at render
  // time, after this function has already returned.
  const handleMaterialChange = (next: PreparedMaterial | undefined) => {
    setMaterial(next);
    if (next) {
      const previous = lastFileName.current;
      setDocument((prev) => (prev.trim() === '' || prev === previous ? next.name : prev));
      lastFileName.current = next.name;
    }
    setMaterialErrorCode(undefined);
    clearMaterialFieldErrors();
  };

  const handleRangeChange = (next: PageRangeInput) => {
    setRange(next);
    if (materialErrorCode && RANGE_CODES.has(materialErrorCode)) {
      setMaterialErrorCode(undefined);
    }
  };

  const handlePhotoSetChange = (next: PhotoSet) => {
    setPhotoSet(next);
    // The section may call this several times in a row, so the pin reads the latest state.
    if (next.photos.length > 0) {
      setPhotoPageLanguage((prev) => prev ?? language);
    }
    setPhotoError(undefined);
    setMaterialErrorCode(undefined);
    clearMaterialFieldErrors();
  };

  const handleMethodChange = (next: JobMethod) => {
    uploadRef.current?.abort();
    setMethod(next);
  };

  /** Checks `body` against the schema, showing its issues on the form's fields (QZ-D48). */
  const parseBody = (body: Record<string, unknown>): CreateJobRequest | undefined => {
    const parsed = createJobRequestSchema.safeParse(body);
    if (!parsed.success) {
      setFields(mapIssues(parsed.error.issues));
      return undefined;
    }
    return parsed.data;
  };

  const postJob = async (data: CreateJobRequest) => {
    try {
      const job = await createJob(data).unwrap();
      // Only a job the API accepted sets the next form's defaults; a refused Start would replace
      // a working set-up with a broken one.
      saveNewJobDefaults({
        method,
        language,
        taskCount: data.taskCount,
        stepModels,
        approval,
        targetLanguages: LANGUAGES.filter((code) => targetLanguages.includes(code)),
        ageGroups: AGE_GROUPS.filter((code) => ageGroups.includes(code)),
        unpickedSites: [...unpicked],
      });
      navigate(`/jobs/${job.jobId}`);
    } catch (thrown) {
      const code = problemCode(thrown);
      if (isMaterialMethod(method) && code && MATERIAL_CODES.has(code)) {
        setMaterialErrorCode(code);
        return;
      }
      setSubmitError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  /** A failure preparing or uploading the photo set, shown on the material field. */
  const failPhotos = (thrown: unknown) => {
    if (!isAbortError(thrown)) {
      setPhotoError(thrown instanceof MaterialPrepError ? thrown.code : 'unknown');
    }
  };

  /** PHO-D6: a photo set's text is uploaded as an extraction at Start, then the job created. */
  const submitPhotos = async (signal: AbortSignal) => {
    let extraction: { id: string; text: StudyMaterialText };
    try {
      // A plain `Error` here is a broken precondition, which Start's rule should have kept.
      extraction = await photoSetExtraction(photoSet, pageLanguage, hashText);
    } catch (thrown) {
      failPhotos(thrown);
      return;
    }
    if (signal.aborted) {
      return;
    }

    const trimmedInstructions = instructions.trim();
    const trimmedSection = section.trim();
    const data = parseBody({
      method: 'photos',
      extractionId: extraction.id,
      materialName: document.trim(),
      ...(trimmedSection && { section: trimmedSection }),
      language,
      taskCount: Number(taskCount),
      stepModels,
      approval,
      targetLanguages: LANGUAGES.filter((code) => targetLanguages.includes(code)),
      ageGroups: AGE_GROUPS.filter((code) => ageGroups.includes(code)),
      ...(trimmedInstructions && { instructions: trimmedInstructions }),
    });
    if (!data) {
      return;
    }

    try {
      await uploadExtraction(
        extraction.id,
        extraction.text,
        { createUpload: (body) => createExtractionUpload(body).unwrap() },
        signal
      );
    } catch (thrown) {
      failPhotos(thrown);
      return;
    }
    if (signal.aborted) {
      return;
    }

    await postJob(data);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFields({});
    setSubmitError(undefined);
    setMaterialErrorCode(undefined);
    setPhotoError(undefined);

    if (method === 'photos') {
      uploadRef.current?.abort();
      const controller = new AbortController();
      uploadRef.current = controller;
      // Set before hashing, which is async, so a second click cannot start a second submit.
      setUploading(true);
      try {
        await submitPhotos(controller.signal);
      } finally {
        // A later submit owns `uploading` once it has replaced this one.
        if (uploadRef.current === controller) {
          uploadRef.current = undefined;
          setUploading(false);
        }
      }
      return;
    }

    let body: Record<string, unknown>;
    if (method === 'prompt') {
      body = {
        request: request.trim(),
        language,
        taskCount: Number(taskCount),
        stepModels,
        approval,
        targetLanguages: LANGUAGES.filter((code) => targetLanguages.includes(code)),
        ageGroups: AGE_GROUPS.filter((code) => ageGroups.includes(code)),
        // Unsent before the list loads, which the API reads as every site.
        ...(sourcesLoaded ? { sourceSites: pickedSites } : {}),
        ...(requestFile ? { requestFile } : {}),
      };
    } else {
      // PDF, photos having returned above. Start is disabled until the PDF is prepared and its
      // range is valid, so this only guards a race.
      if (material === undefined || parsedRange === undefined) {
        return;
      }
      const { from, to } = parsedRange;
      const trimmedSection = section.trim();
      // QZ-D81: a range edge left at the PDF's own edge is not sent.
      body = {
        method: 'pdf',
        extractionId: material.extractionId,
        materialName: document.trim(),
        ...(trimmedSection && { section: trimmedSection }),
        ...(from !== 1 && { pageFrom: from }),
        ...(to !== material.pdfPageCount && { pageTo: to }),
        language,
        taskCount: Number(taskCount),
        stepModels,
        approval,
        targetLanguages: LANGUAGES.filter((code) => targetLanguages.includes(code)),
        ageGroups: AGE_GROUPS.filter((code) => ageGroups.includes(code)),
      };
      const trimmedInstructions = instructions.trim();
      if (trimmedInstructions) {
        body.instructions = trimmedInstructions;
      }
    }

    const data = parseBody(body);
    if (data) {
      await postJob(data);
    }
  };

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <Link className="qz-back" to="/jobs">
          <Icon name="arrow-left" /> {t('job.page.back')}
        </Link>

        <div className="qz-page-header">
          <div>
            <h1>{t('job.new.heading')}</h1>
            <p className="qz-sub">{t('job.new.sub')}</p>
          </div>
        </div>

        {/* noValidate: the min/max on task count are a UX hint, not a gate — zod owns validation. */}
        <form className="qz-card" onSubmit={handleSubmit} noValidate>
          <div className="qz-card-body qz-stack">
            <div>
              <div className="qz-label mb-2">{t('job.method.heading')}</div>
              <div className="qz-radio-cards">
                <div className="qz-radio-card">
                  <input
                    type="radio"
                    name="method"
                    id="job-method-prompt"
                    checked={method === 'prompt'}
                    onChange={() => handleMethodChange('prompt')}
                  />
                  <label htmlFor="job-method-prompt">
                    <strong>
                      <Icon name="message" /> {t('job.method.prompt')}
                    </strong>
                    <span>{t('job.method.promptHint')}</span>
                  </label>
                </div>
                <div className="qz-radio-card">
                  <input
                    type="radio"
                    name="method"
                    id="job-method-pdf"
                    checked={method === 'pdf'}
                    onChange={() => handleMethodChange('pdf')}
                  />
                  <label htmlFor="job-method-pdf">
                    <strong>
                      <Icon name="file-pdf" /> {t('job.method.pdf')}
                    </strong>
                    <span>{t('job.method.pdfHint')}</span>
                  </label>
                </div>
                <div className="qz-radio-card">
                  <input
                    type="radio"
                    name="method"
                    id="job-method-photos"
                    checked={method === 'photos'}
                    onChange={() => handleMethodChange('photos')}
                  />
                  <label htmlFor="job-method-photos">
                    <strong>
                      <Icon name="images" /> {t('job.method.photos')}
                    </strong>
                    <span>{t('job.method.photosHint')}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-sm-6">
                <label htmlFor="job-language" className="form-label">
                  {t('job.field.language')}
                </label>
                <select
                  id="job-language"
                  className={`form-select${fields['job-language'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-language'] ? 'job-language-error' : undefined}
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value as Language)}
                >
                  {LANGUAGES.map((code) => (
                    <option key={code} value={code}>
                      {t(`task.language.${code}`)}
                    </option>
                  ))}
                </select>
                <div className="form-text">{t('job.new.languageHelp')}</div>
                <FieldError id="job-language" messages={fields['job-language']} />
              </div>
              <div className="col-sm-6">
                <label htmlFor="job-task-count" className="form-label">
                  {t('job.field.taskCount')}
                </label>
                <input
                  id="job-task-count"
                  type="number"
                  min={1}
                  max={JOB_TASK_COUNT_MAX}
                  step={1}
                  className={`form-control${fields['job-task-count'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-task-count'] ? 'job-task-count-error' : undefined}
                  value={taskCount}
                  onChange={(e) => setTaskCount(e.target.value)}
                />
                <div className="form-text">{t('job.new.taskCountHelp')}</div>
                <FieldError id="job-task-count" messages={fields['job-task-count']} />
              </div>
            </div>

            <StepModelsField
              value={stepModels}
              onChange={setStepModels}
              error={fields['job-step-models']?.[0]}
              disabled={isLoading}
            />

            <fieldset id="job-targetLanguages">
              <legend className="form-label">{t('job.field.targetLanguages')}</legend>
              <div className="qz-chips">
                {LANGUAGES.map((code) => (
                  <span className="qz-chip" key={code}>
                    <input
                      id={`job-targetLanguages-${code}`}
                      type="checkbox"
                      checked={targetLanguages.includes(code)}
                      disabled={code === language}
                      onChange={(e) => toggleTargetLanguage(code, e.target.checked)}
                    />
                    <label
                      htmlFor={`job-targetLanguages-${code}`}
                      title={t(`task.language.${code}`)}
                    >
                      <span aria-hidden="true">{code.toUpperCase()}</span>
                      <span className="visually-hidden">{t(`task.language.${code}`)}</span>
                    </label>
                  </span>
                ))}
              </div>
              <div className="form-text">{t('job.new.targetLanguagesHelp')}</div>
              <FieldError id="job-targetLanguages" messages={fields['job-targetLanguages']} />
            </fieldset>

            <fieldset id="job-ageGroups">
              <legend className="form-label">{t('job.field.ageGroups')}</legend>
              <div className="qz-chips">
                {AGE_GROUPS.map((ageGroup) => (
                  <span className="qz-chip" key={ageGroup}>
                    <input
                      id={`job-ageGroups-${ageGroup}`}
                      type="checkbox"
                      checked={ageGroups.includes(ageGroup)}
                      onChange={(e) => toggleAgeGroup(ageGroup, e.target.checked)}
                    />
                    <label htmlFor={`job-ageGroups-${ageGroup}`}>
                      {t(`task.ageGroup.${ageGroup}`)}
                    </label>
                  </span>
                ))}
              </div>
              <div className="form-text">{t('job.new.ageGroupsHelp')}</div>
              <FieldError id="job-ageGroups" messages={fields['job-ageGroups']} />
            </fieldset>

            <fieldset id="job-approval">
              <legend className="form-label">{t('job.approval.label')}</legend>
              <div className="qz-radio-cards">
                {JOB_APPROVALS.map((value) => (
                  <div className="qz-radio-card" key={value}>
                    <input
                      type="radio"
                      name="approval"
                      id={`job-approval-${value}`}
                      checked={approval === value}
                      onChange={() => setApproval(value)}
                    />
                    <label htmlFor={`job-approval-${value}`}>
                      <strong>{t(`job.approval.${value}`)}</strong>
                    </label>
                  </div>
                ))}
              </div>
              <div className="form-text">{t('job.approval.hint')}</div>
              <FieldError id="job-approval" messages={fields['job-approval']} />
            </fieldset>

            {method === 'prompt' && (
              <div>
                <label htmlFor="job-request" className="form-label">
                  {t('job.field.request')}
                </label>
                <textarea
                  id="job-request"
                  rows={4}
                  maxLength={JOB_REQUEST_MAX}
                  className={`form-control${fields['job-request'] ? ' is-invalid' : ''}`}
                  aria-describedby={fields['job-request'] ? 'job-request-error' : undefined}
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                />
                <div className="form-text">{t('job.new.requestHelp')}</div>
                <FieldError id="job-request" messages={fields['job-request']} />

                <RequestFileField
                  value={requestFile}
                  onChange={setRequestFile}
                  error={fields['job-requestFile']?.[0]}
                  disabled={isLoading}
                />

                <fieldset className="mt-3">
                  <legend className="form-label">{t('job.new.researchesOn')}</legend>
                  <div className="qz-chips">
                    {sources.map(({ domain }) => (
                      <span className="qz-chip" key={domain}>
                        <input
                          id={`job-source-${domain}`}
                          type="checkbox"
                          checked={!unpicked.has(domain)}
                          onChange={(e) => toggleSite(domain, e.target.checked)}
                        />
                        <label htmlFor={`job-source-${domain}`}>{domain}</label>
                      </span>
                    ))}
                  </div>
                  <div className="form-text">
                    {openWeb && <>{t('job.new.openWeb')} </>}
                    <Link to="/sources">{t('job.new.editSources')}</Link>
                  </div>
                  <FieldError id="job-sources" messages={fields['job-sources']} />
                </fieldset>
              </div>
            )}

            {method === 'pdf' && (
              <PdfMaterialSection
                value={material}
                onChange={handleMaterialChange}
                range={range}
                onRangeChange={handleRangeChange}
                error={
                  materialErrorCode ? t(`error.${materialErrorCode}`) : fields['job-material']?.[0]
                }
                disabled={isLoading}
                jobLanguage={language}
              />
            )}

            {method === 'photos' && (
              <PhotoMaterialSection
                value={photoSet}
                onChange={handlePhotoSetChange}
                pageLanguage={pageLanguage}
                onPageLanguageChange={setPhotoPageLanguage}
                disabled={isLoading || uploading}
                error={
                  photoError
                    ? t(`error.${photoError}`)
                    : materialErrorCode
                      ? t(`error.${materialErrorCode}`)
                      : fields['job-material']?.[0]
                }
              />
            )}

            {isMaterialMethod(method) && (
              <MaterialNameFields
                document={document}
                onDocumentChange={setDocument}
                section={section}
                onSectionChange={setSection}
                documents={documentsData?.documents ?? []}
                documentError={fields['job-document']?.[0]}
                sectionError={fields['job-section']?.[0]}
                disabled={isLoading || uploading}
              />
            )}

            {isMaterialMethod(method) && (
              <div>
                <label htmlFor="job-instructions" className="form-label">
                  {t('job.field.instructions')}
                </label>
                <textarea
                  id="job-instructions"
                  rows={2}
                  maxLength={JOB_REQUEST_MAX}
                  className={`form-control${fields['job-instructions'] ? ' is-invalid' : ''}`}
                  aria-describedby={
                    fields['job-instructions'] ? 'job-instructions-error' : undefined
                  }
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
                <div className="form-text">{t('job.new.instructionsHelp')}</div>
                <FieldError id="job-instructions" messages={fields['job-instructions']} />
              </div>
            )}

            {submitError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{submitError}</div>
              </div>
            )}
          </div>

          <div className="qz-card-footer d-flex flex-wrap align-items-center gap-2">
            <span className="qz-small qz-muted qz-footer-note">
              <Icon name="circle-info" /> {t('job.new.costNote')}
            </span>
            <Link to="/jobs" className="btn btn-outline-secondary ms-auto">
              {t('job.new.cancel')}
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isLoading ||
                uploading ||
                (method === 'pdf' && !pdfReady) ||
                (method === 'photos' && !photosReady)
              }
            >
              <Icon name="wand-magic-sparkles" /> {t('job.new.start')}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default NewJobPage;
